import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiService } from '../services/apiService';
import { proxyService } from '../services/proxyService';
import { HelpPageKey, getHelpContextForPage } from '../utils/helpPageMapping';
import { isAgentFailureMessage, isAgentSuccessMessage } from '../utils/agentChatMessageStyle';
import {
  AGENT_CHAT_PAGE_SIZE,
  AGENT_LOCAL_WELCOME_ACTIONS,
  AGENT_LOCAL_WELCOME_TEXT,
  createAgentChatForNewProject,
  ensureAgentChatForProject,
  filterAgentActionsForWidget,
  mapAgentHistoryEntries,
  pickAgentChatForProject,
} from '../utils/agentChatBootstrap';
import {
  buildAgentWidgetQuickActions,
  defaultAgentWidgetQuickActionId,
} from '../utils/agentIntentOptions';
import { resolveAgentCollectionsSelection, readAgentCollectionsSelection } from '../utils/agentCollectionsSelection';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { formatAgentUserMessageText, formatAgentActionLabel } from '../utils/agentChatMessageFormat';
import { normalizeAgentChatMarkdown } from '../utils/agentChatMarkdown';
import {
  navigationStateFromAgentAction,
  navigationStateFromShortcut,
  dispatchAgentPageNavigation,
  dispatchAgentContextSync,
  AGENT_CONTEXT_SYNC_EVENT,
  type AgentContextSyncDetail,
  type AgentPageNavigationState,
} from '../utils/agentPageNavigation';
import { interpretAgentShortcut } from '../utils/agentShortcut';
import {
  getAgentInfoHelpAnswer,
  isGenericHelpResponse,
} from '../utils/agentInfoHelp';
import {
  isIntentConfirmAction,
  rememberOfferedAgentIntentId,
  withAgentIntentContextPayload,
  readLastOfferedAgentIntentId,
} from '../utils/agentIntentContext';
import AgentExecutionResultMessage, { parseAgentExecutionResult } from './AgentExecutionResultMessage';
import AgentWidgetCreateSelectProjectForm, {
  mapAgentReplyToWidgetMessage,
} from './AgentWidgetCreateSelectProjectForm';
import AgentWidgetImportSwaggerForm from './AgentWidgetImportSwaggerForm';
import AgentWidgetCreateEnvironmentForm from './AgentWidgetCreateEnvironmentForm';
import AgentWidgetEnvVariableForm from './AgentWidgetEnvVariableForm';
import AgentWidgetApplyEnvToRequestForm from './AgentWidgetApplyEnvToRequestForm';
import AgentWidgetExecuteRequestForm from './AgentWidgetExecuteRequestForm';
import AgentWidgetCopyRequestToAllCollectionsForm from './AgentWidgetCopyRequestToAllCollectionsForm';
import AgentWidgetProjectPickForm from './AgentWidgetProjectPickForm';
import AgentWidgetCreateFunctionForm from './AgentWidgetCreateFunctionForm';
import AgentWidgetRunFunctionForm from './AgentWidgetRunFunctionForm';
import { AgentInlineSelect } from './ai-agent-ui';
import { APPLY_ENV_ALL_KEYS } from '../utils/agentApplyEnvUtils';
import { AGENT_SCOPE_ALL, type AgentScopeAll } from '../utils/agentSuggestAssertionsScope';
import {
  isWidgetInlineProjectPickAction,
  WIDGET_INLINE_PROJECT_PICK_ACTIONS,
} from '../utils/agentProjectPickMeta';
import type { AgentProjectPickAction } from '../utils/agentShortcut';
import { notifyAgentSideEffectsFromAgent } from '../utils/environmentVariablesEvents';
import { runProxyDiagnostics } from '../utils/proxyDiagnostics';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import type { AgentActionButton, AgentChatEntry, AgentMessageResponse, AgentSessionState } from '../types/agent';

type MessageAuthor = 'user' | 'assistant';

interface ChatMessage {
  id: number;
  author: MessageAuthor;
  text: string;
  intentMode?: string;
  confidence?: number;
  lowConfidence?: boolean;
  citations?: { title: string; helpPath: string }[];
  actions?: { id: string; label: string; targetPath: string }[];
  agentActions?: AgentActionButton[];
  agentFailure?: boolean;
  agentSuccess?: boolean;
  hideFeedback?: boolean;
  /** Persisted agent chat row id (for paginated history). */
  serverMessageId?: number;
  feedbackSubmitted?: boolean;
}

const HELP_CHAT_SESSION_KEY = 'helpChatWidgetSessionId';
const HELP_CHAT_OPEN_KEY = 'helpChatWidgetOpen';
const FAB_OFFSET = 16;
const BUTTON_SIZE = 48;
/** Fixed width of the right assistant strip (VS Code–style secondary sidebar). */
export const ASSISTANT_STRIP_WIDTH_PX = 340;
const ASSISTANT_STRIP_MAX_VW = 'min(340px, 30vw)';
/** App header height — strip starts below the navbar. */
export const ASSISTANT_STRIP_TOP_PX = 64;

interface HelpChatWidgetProps {
  /** Reserved for layout integration; strip width is fixed. */
  sidebarOpen?: boolean;
  /** Notifies layout shell to reserve horizontal space when the strip is open. */
  onOpenChange?: (open: boolean) => void;
}

function loadSavedOpenState(): boolean {
  try {
    const raw = localStorage.getItem(HELP_CHAT_OPEN_KEY);
    if (raw == null) return false;
    return raw === 'true';
  } catch {
    return false;
  }
}

function createHelpSessionId(): string {
  return `help-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const resolvePageKey = (pathname: string): HelpPageKey => {
  if (pathname.startsWith('/projects')) return 'PROJECTS';
  if (pathname.startsWith('/collections')) return 'COLLECTIONS';
  if (pathname.startsWith('/environments')) return 'ENVIRONMENTS';
  if (pathname.startsWith('/env-variables')) return 'ENVIRONMENTS';
  if (pathname.startsWith('/functions')) return 'FUNCTIONS';
  if (pathname.startsWith('/swagger')) return 'SWAGGER';
   if (pathname.startsWith('/manage-account')) return 'MANAGE_ACCOUNT';
  return 'DASHBOARD';
};

const MarkdownMessage: React.FC<{ text: string; tone?: 'default' | 'error' | 'success' }> = ({
  text,
  tone = 'default',
}) => (
  <Box
    sx={{
      lineHeight: 1.43,
      letterSpacing: '0.01em',
      color: tone === 'error' ? 'error.main' : tone === 'success' ? 'success.main' : 'text.primary',
      '& p': { m: 0, mb: 0.75 },
      '& p:last-child': { mb: 0 },
      '& h1, & h2, & h3, & h4': {
        mt: 1.1,
        mb: 0.55,
        lineHeight: 1.35,
        fontWeight: 700,
      },
      '& h1': { fontSize: '1.05rem' },
      '& h2': { fontSize: '1rem' },
      '& h3': {
        fontSize: '0.95rem',
        pl: 0.7,
        borderLeft: '3px solid',
        borderColor: 'primary.main',
      },
      '& h4': { fontSize: '0.92rem' },
      '& ul, & ol': { mt: 0.45, mb: 0.8, pl: 2.05 },
      '& li': { mb: 0.45, pl: 0.15 },
      '& ul li::marker': { color: 'primary.main' },
      '& ol li::marker': { color: 'primary.main', fontWeight: 600 },
      // ReactMarkdown can wrap each list item line in <p>, causing "double spacing".
      '& li > p': { display: 'inline', m: 0 },
      '& li p': { m: 0 },
      '& strong': {
        fontWeight: 700,
        color: tone === 'error' ? 'error.main' : tone === 'success' ? 'success.main' : 'text.primary',
      },
      '& blockquote': {
        m: 0,
        my: 0.9,
        px: 1.2,
        py: 0.6,
        borderLeft: '3px solid',
        borderColor: 'primary.main',
        bgcolor: 'rgba(25,118,210,0.08)',
        borderRadius: 1,
      },
      '& code': {
        fontFamily: 'monospace',
        fontSize: '0.78rem',
        bgcolor: 'rgba(0,0,0,0.08)',
        px: 0.4,
        py: 0.1,
        borderRadius: 0.5,
      },
      '& pre': {
        overflowX: 'auto',
        p: 1.1,
        borderRadius: 1.5,
        bgcolor: 'rgba(0,0,0,0.75)',
        color: '#f5f5f5',
      },
      '& pre code': { bgcolor: 'transparent', p: 0, color: 'inherit' },
      '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        my: 1.1,
        fontSize: '0.8rem',
      },
      '& th, & td': {
        border: '1px solid rgba(0,0,0,0.14)',
        px: 0.8,
        py: 0.5,
        textAlign: 'left',
      },
      '& th': {
        bgcolor: 'rgba(25,118,210,0.08)',
        fontWeight: 700,
      },
      '& hr': {
        border: 0,
        height: 1,
        my: 1,
        bgcolor: 'rgba(0,0,0,0.12)',
      },
    }}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeAgentChatMarkdown(text)}</ReactMarkdown>
  </Box>
);

const HelpChatWidget: React.FC<HelpChatWidgetProps> = ({ onOpenChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProject, projects, refreshProjects, setSelectedProject } = useProjects();
  const { user } = useAuth();

  const [open, setOpen] = useState<boolean>(() => loadSavedOpenState());
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [helpFollowUps, setHelpFollowUps] = useState<string[]>([]);
  const [initializedFromAgent, setInitializedFromAgent] = useState(false);
  const [agentMode, setAgentMode] = useState(false);
  const [agentChatId, setAgentChatId] = useState<number | null>(null);
  const [agentSessionState, setAgentSessionState] = useState<AgentSessionState | null>(null);
  const agentProjectIdRef = useRef<number | null>(null);
  const agentChatIdRef = useRef<number | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const skipScrollToBottomRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const initialAgentHistoryReadyRef = useRef(false);
  const enableOlderLoadTimerRef = useRef<number | null>(null);
  const [hasMoreOlderAgentMessages, setHasMoreOlderAgentMessages] = useState(false);
  const [loadingOlderAgentMessages, setLoadingOlderAgentMessages] = useState(false);
  const [loadingAgentHistory, setLoadingAgentHistory] = useState(false);
  const [agentQuickAction, setAgentQuickAction] = useState('');
  const [widgetInlinePanel, setWidgetInlinePanel] = useState<
    | 'create_select'
    | 'import_swagger'
    | 'create_environment'
    | 'env_variable'
    | 'apply_env_to_request'
    | 'execute_request'
    | 'copy_request_to_all_collections'
    | 'project_pick'
    | 'create_function'
    | 'run_function'
    | null
  >(null);
  const [widgetCreateSelectMode, setWidgetCreateSelectMode] = useState<'new' | 'existing'>('new');
  const [createEnvironmentDefaults, setCreateEnvironmentDefaults] = useState<{
    projectId?: number | '';
    projectName?: string;
  }>({});
  const [envVariableDefaults, setEnvVariableDefaults] = useState<{
    projectId?: number | '';
    projectName?: string;
    environmentId?: number | '';
    key?: string;
    value?: string;
  }>({});
  const [applyEnvToRequestDefaults, setApplyEnvToRequestDefaults] = useState<{
    projectId?: number | '';
    projectName?: string;
    collectionId?: number | '';
    requestId?: number | '';
    environmentId?: number | '';
    key?: string;
  }>({});
  const [executeRequestDefaults, setExecuteRequestDefaults] = useState<{
    projectId?: number | '';
    projectName?: string;
    collectionId?: number | '';
    requestId?: number | '';
    environmentId?: number | '';
  }>({});
  const [projectPickAction, setProjectPickAction] = useState<AgentProjectPickAction | null>(null);
  const [projectPickDefaults, setProjectPickDefaults] = useState<{
    projectId?: number | '';
    projectName?: string;
    collectionId?: number | AgentScopeAll;
    requestId?: number | AgentScopeAll;
  }>({});
  const [importSwaggerDefaults, setImportSwaggerDefaults] = useState<{
    projectId?: number | '';
    projectName?: string;
    swaggerUrl?: string;
  }>({});
  const [createFunctionDefaults, setCreateFunctionDefaults] = useState<{ name?: string }>({});
  const [runFunctionDefaults, setRunFunctionDefaults] = useState<{
    name?: string;
    args?: unknown[];
  }>({});
  const [helpSessionId] = useState<string>(() => {
    try {
      const existing = localStorage.getItem(HELP_CHAT_SESSION_KEY);
      if (existing && existing.trim()) return existing;
      const next = createHelpSessionId();
      localStorage.setItem(HELP_CHAT_SESSION_KEY, next);
      return next;
    } catch {
      return createHelpSessionId();
    }
  });

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const pageKey = useMemo<HelpPageKey>(
    () => resolvePageKey(location.pathname),
    [location.pathname]
  );

  const helpContext = useMemo(() => getHelpContextForPage(pageKey), [pageKey]);

  // Follow-ups from a previous answer belong to that page's topic; clear them when the route
  // changes so chips and quick prompts match the current area (e.g. not Collections after leaving Collections).
  useEffect(() => {
    setHelpFollowUps([]);
  }, [pageKey]);

  useEffect(() => {
    if (!agentMode) return;
    setAgentQuickAction(defaultAgentWidgetQuickActionId(pageKey));
    setWidgetInlinePanel(null);
    setProjectPickAction(null);
  }, [pageKey, agentMode]);

  const suggestionExamples = useMemo(() => {
    switch (pageKey) {
      case 'PROJECTS':
        return [
          'How do I create a project?',
          'What are projects used for?',
        ];
      case 'COLLECTIONS':
        return [
          'How do I create a collection?',
          'How do I configure a request?',
        ];
      case 'ENVIRONMENTS':
        return [
          'How do I create an environment?',
          'How do I switch environments for a request?',
        ];
      case 'SWAGGER':
        return [
          'What are the steps of Swagger API import?',
          'How do I import an OpenAPI spec?',
        ];
      case 'MANAGE_ACCOUNT':
        return [
          'What can I configure in Manage Account?',
          'How do I add a team member?',
        ];
      case 'FUNCTIONS':
        return [
          'How do I use functions in a request?',
          'How do dynamic variables work?',
        ];
      case 'DASHBOARD':
      default:
        return [
          'How do I create a project?',
          'What are collections?',
        ];
    }
  }, [pageKey]);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  const buildAgentWelcomeMessages = (): ChatMessage[] => [
    {
      id: 1,
      author: 'assistant',
      text: AGENT_LOCAL_WELCOME_TEXT,
      agentActions: AGENT_LOCAL_WELCOME_ACTIONS,
      hideFeedback: true,
    },
  ];

  const mapAgentEntriesToMessages = (entries: AgentChatEntry[], idOffset = 0): ChatMessage[] => {
    let nextId = idOffset + 1;
    return entries.map((m) => {
      const actions = m.role === 'agent' ? m.actions : undefined;
      const failure = m.role === 'agent' && isAgentFailureMessage(m.text, actions);
      const success = m.role === 'agent' && !failure && isAgentSuccessMessage(m.text, actions);
      const msg: ChatMessage = {
        id: nextId++,
        serverMessageId: m.id,
        author: m.role === 'user' ? 'user' : 'assistant',
        text: m.role === 'user' ? formatAgentUserMessageText(m.text) : m.text,
        agentActions: actions,
        agentFailure: failure,
        agentSuccess: success,
        hideFeedback: true,
      };
      return msg;
    });
  };

  const scrollAgentChatToBottom = (behavior: ScrollBehavior = 'auto') => {
    const el = messagesScrollRef.current;
    if (el) {
      isProgrammaticScrollRef.current = true;
      el.scrollTop = el.scrollHeight;
      window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 0);
      return;
    }
    if (behavior === 'smooth') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    }
  };

  const scheduleAgentScrollToBottom = () => {
    const attempt = () => scrollAgentChatToBottom('auto');
    requestAnimationFrame(() => {
      attempt();
      requestAnimationFrame(() => {
        attempt();
        window.setTimeout(attempt, 0);
        window.setTimeout(attempt, 50);
        window.setTimeout(attempt, 150);
      });
    });
  };

  const scheduleEnableOlderAgentMessageLoad = () => {
    if (enableOlderLoadTimerRef.current != null) {
      window.clearTimeout(enableOlderLoadTimerRef.current);
    }
    initialAgentHistoryReadyRef.current = false;
    enableOlderLoadTimerRef.current = window.setTimeout(() => {
      initialAgentHistoryReadyRef.current = true;
      enableOlderLoadTimerRef.current = null;
    }, 350);
  };

  const clearAgentQuickAction = () => setAgentQuickAction('');

  const loadAgentChatHistory = async (chatId: number, replaceMessages: boolean) => {
    setLoadingAgentHistory(replaceMessages);
    setHasMoreOlderAgentMessages(false);
    initialAgentHistoryReadyRef.current = false;
    if (enableOlderLoadTimerRef.current != null) {
      window.clearTimeout(enableOlderLoadTimerRef.current);
      enableOlderLoadTimerRef.current = null;
    }
    if (replaceMessages) {
      setMessages([]);
      clearAgentQuickAction();
      setWidgetInlinePanel(null);
      setProjectPickAction(null);
    }
    try {
      const history = await apiService.agentGetChatHistory(chatId, {
        limit: AGENT_CHAT_PAGE_SIZE,
      });
      const entries = mapAgentHistoryEntries(history.messages ?? []);
      const mapped = mapAgentEntriesToMessages(entries);
      setMessages(mapped.length > 0 ? mapped : buildAgentWelcomeMessages());
      setHasMoreOlderAgentMessages(Boolean(history.hasMoreOlder));
    } finally {
      setLoadingAgentHistory(false);
    }
  };

  const loadOlderAgentMessages = async () => {
    if (!agentChatId || loadingOlderAgentMessages || !hasMoreOlderAgentMessages || !agentMode) return;
    const oldest = messages.find((m) => m.serverMessageId != null);
    const beforeId = oldest?.serverMessageId;
    if (beforeId == null) return;

    const scrollEl = messagesScrollRef.current;
    const prevHeight = scrollEl?.scrollHeight ?? 0;
    const prevTop = scrollEl?.scrollTop ?? 0;

    setLoadingOlderAgentMessages(true);
    skipScrollToBottomRef.current = true;
    try {
      const history = await apiService.agentGetChatHistory(agentChatId, {
        limit: AGENT_CHAT_PAGE_SIZE,
        beforeId,
      });
      const entries = mapAgentHistoryEntries(history.messages ?? []);
      const older = mapAgentEntriesToMessages(entries, 0);
      if (older.length > 0) {
        setMessages((prev) => {
          const idBase = older.length;
          const shifted = prev.map((m, i) => ({ ...m, id: idBase + i + 1 }));
          const merged = older.map((m, i) => ({ ...m, id: i + 1 }));
          return [...merged, ...shifted];
        });
      }
      setHasMoreOlderAgentMessages(Boolean(history.hasMoreOlder));
      requestAnimationFrame(() => {
        if (scrollEl) {
          isProgrammaticScrollRef.current = true;
          scrollEl.scrollTop = scrollEl.scrollHeight - prevHeight + prevTop;
          window.setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 0);
        }
      });
    } finally {
      setLoadingOlderAgentMessages(false);
    }
  };

  const handleAgentMessagesScroll = () => {
    const el = messagesScrollRef.current;
    if (
      !el ||
      !agentMode ||
      !initialAgentHistoryReadyRef.current ||
      isProgrammaticScrollRef.current ||
      loadingOlderAgentMessages ||
      !hasMoreOlderAgentMessages ||
      loadingAgentHistory
    ) {
      return;
    }
    if (el.scrollTop < 96) {
      void loadOlderAgentMessages();
    }
  };

  const loadAgentModeContext = React.useCallback(async (preferredChatId?: number | null) => {
    setLoading(true);
    try {
      const [session, chats] = await Promise.all([
        apiService.agentGetSession(),
        apiService.agentListChats(),
      ]);
      setAgentSessionState(session ?? null);

      if (preferredChatId != null) {
        setAgentChatId(preferredChatId);
        if (selectedProject) {
          agentProjectIdRef.current = selectedProject.id;
        }
        await loadAgentChatHistory(preferredChatId, true);
        return;
      }

      if (!selectedProject) {
        setAgentChatId(null);
        agentProjectIdRef.current = null;
        setHasMoreOlderAgentMessages(false);
        setMessages(buildAgentWelcomeMessages());
        return;
      }

      const picked = pickAgentChatForProject(chats, selectedProject, session);
      let targetChatId = picked?.id ?? null;

      if (targetChatId == null) {
        const ensured = await ensureAgentChatForProject(selectedProject, session, {
          userId: user?.id,
        });
        targetChatId = ensured.chatId;
      }
      setAgentChatId(targetChatId);
      agentProjectIdRef.current = selectedProject.id;
      await loadAgentChatHistory(targetChatId, true);
    } catch {
      setHasMoreOlderAgentMessages(false);
      setMessages([
        {
          id: 1,
          author: 'assistant',
          text: 'Sorry, could not load AI Agent chat. Try again or open the full AI Agent page.',
          agentFailure: true,
          hideFeedback: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, user?.id]);

  const handleNewAgentChat = async () => {
    if (!agentMode || loading) return;
    setWidgetInlinePanel(null);
    setImportSwaggerDefaults({});
    setAgentQuickAction('');
    setHasMoreOlderAgentMessages(false);
    initialAgentHistoryReadyRef.current = false;
    setLoading(true);
    try {
      await apiService.agentClearSession();
      const created = await apiService.agentCreateChat();
      setAgentChatId(created.id);
      setAgentSessionState(null);
      agentProjectIdRef.current = selectedProject?.id ?? null;
      setMessages(buildAgentWelcomeMessages());
      dispatchAgentContextSync({
        chatId: created.id,
        projectId: selectedProject?.id ?? null,
        source: 'widget',
      });
    } catch {
      setMessages([
        {
          id: 1,
          author: 'assistant',
          text: 'Sorry, could not start a new chat. Try again.',
          agentFailure: true,
          hideFeedback: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const switchAssistantMode = async (nextAgentMode: boolean) => {
    if (nextAgentMode === agentMode) return;
    setAgentMode(nextAgentMode);
    setHelpFollowUps([]);
    setWidgetInlinePanel(null);
    setImportSwaggerDefaults({});
    if (!nextAgentMode) {
      clearAgentQuickAction();
      setMessages([]);
      setAgentChatId(null);
      setAgentSessionState(null);
      agentProjectIdRef.current = null;
      setHasMoreOlderAgentMessages(false);
      initialAgentHistoryReadyRef.current = false;
      return;
    }
    setMessages([]);
    setHasMoreOlderAgentMessages(false);
    initialAgentHistoryReadyRef.current = false;
    agentProjectIdRef.current = selectedProject?.id ?? null;
    await loadAgentModeContext(agentChatId);
    setAgentQuickAction(defaultAgentWidgetQuickActionId(pageKey));
  };

  useEffect(() => {
    const onContextSync = (event: Event) => {
      const detail = (event as CustomEvent<AgentContextSyncDetail>).detail;
      if (!detail?.chatId || detail.source === 'widget') return;

      setAgentChatId(detail.chatId);
      if (detail.projectId != null) {
        agentProjectIdRef.current = detail.projectId;
      }

      if (!agentMode) return;
      clearAgentQuickAction();
      setWidgetInlinePanel(null);
      setProjectPickAction(null);
      void loadAgentChatHistory(detail.chatId, true);
    };

    window.addEventListener(AGENT_CONTEXT_SYNC_EVENT, onContextSync);
    return () => window.removeEventListener(AGENT_CONTEXT_SYNC_EVENT, onContextSync);
  }, [agentMode]);

  useEffect(() => {
    if (!agentMode) return;
    const pid = selectedProject?.id ?? null;
    if (agentProjectIdRef.current === pid) return;
    void loadAgentModeContext();
  }, [selectedProject?.id, agentMode, loadAgentModeContext]);

  useEffect(() => {
    try {
      localStorage.setItem(HELP_CHAT_OPEN_KEY, open ? 'true' : 'false');
    } catch {
      // ignore storage failures
    }
  }, [open]);

  const closeWidgetInlinePanel = () => {
    setWidgetInlinePanel(null);
    setProjectPickAction(null);
  };

  const navigateToAgentPage = (state: AgentPageNavigationState) => {
    const fullState: AgentPageNavigationState = {
      ...state,
      chatId: state.chatId ?? agentChatId ?? undefined,
    };
    dispatchAgentPageNavigation(fullState);
    const onAgentPage = location.pathname === '/ai-agent' || location.pathname.endsWith('/ai-agent');
    if (!onAgentPage) {
      navigate('/ai-agent', { state: fullState });
    }
  };

  const openWidgetCreateSelectForm = (mode: 'new' | 'existing' = 'new') => {
    setOpen(true);
    setWidgetCreateSelectMode(mode);
    setWidgetInlinePanel('create_select');
    setImportSwaggerDefaults({});
  };

  const openWidgetCreateEnvironmentForm = (defaults?: {
    projectId?: number | '';
    projectName?: string;
  }) => {
    const resolvedProjectId =
      defaults?.projectId != null && defaults.projectId !== ''
        ? defaults.projectId
        : createEnvironmentDefaults.projectId != null && createEnvironmentDefaults.projectId !== ''
          ? createEnvironmentDefaults.projectId
          : agentSessionState?.projectId ?? selectedProject?.id ?? '';
    const resolvedProjectName =
      defaults?.projectName?.trim() ||
      createEnvironmentDefaults.projectName?.trim() ||
      agentSessionState?.projectName?.trim() ||
      selectedProject?.name ||
      '';
    void refreshProjects();
    setOpen(true);
    setWidgetInlinePanel('create_environment');
    setCreateEnvironmentDefaults({
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
    });
  };

  const openWidgetEnvVariableForm = (defaults?: {
    projectId?: number | '';
    projectName?: string;
    environmentId?: number | '';
    key?: string;
    value?: string;
  }) => {
    const resolvedProjectId =
      defaults?.projectId != null && defaults.projectId !== ''
        ? defaults.projectId
        : envVariableDefaults.projectId != null && envVariableDefaults.projectId !== ''
          ? envVariableDefaults.projectId
          : agentSessionState?.projectId ?? selectedProject?.id ?? '';
    const resolvedProjectName =
      defaults?.projectName?.trim() ||
      envVariableDefaults.projectName?.trim() ||
      agentSessionState?.projectName?.trim() ||
      selectedProject?.name ||
      '';
    const resolvedEnvironmentId =
      defaults?.environmentId != null && defaults.environmentId !== ''
        ? defaults.environmentId
        : envVariableDefaults.environmentId != null && envVariableDefaults.environmentId !== ''
          ? envVariableDefaults.environmentId
          : agentSessionState?.environmentId ?? '';
    void refreshProjects();
    setOpen(true);
    setWidgetInlinePanel('env_variable');
    setEnvVariableDefaults({
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
      environmentId: resolvedEnvironmentId,
      key: defaults?.key ?? envVariableDefaults.key ?? '',
      value: defaults?.value ?? envVariableDefaults.value ?? '',
    });
  };

  const openWidgetApplyEnvToRequestForm = async (defaults?: {
    projectId?: number | '';
    projectName?: string;
    collectionId?: number | '';
    requestId?: number | '';
    environmentId?: number | '';
    key?: string;
  }) => {
    const collectionsSelection =
      defaults?.collectionId != null || defaults?.requestId != null
        ? readAgentCollectionsSelection()
        : (await resolveAgentCollectionsSelection()) ?? readAgentCollectionsSelection();
    const resolvedProjectId =
      defaults?.projectId != null && defaults.projectId !== ''
        ? defaults.projectId
        : collectionsSelection?.projectId != null
          ? collectionsSelection.projectId
          : applyEnvToRequestDefaults.projectId != null && applyEnvToRequestDefaults.projectId !== ''
            ? applyEnvToRequestDefaults.projectId
            : agentSessionState?.projectId ?? selectedProject?.id ?? '';
    const resolvedProjectName =
      defaults?.projectName?.trim() ||
      collectionsSelection?.projectName?.trim() ||
      applyEnvToRequestDefaults.projectName?.trim() ||
      agentSessionState?.projectName?.trim() ||
      selectedProject?.name ||
      '';
    const resolvedEnvironmentId =
      defaults?.environmentId != null && defaults.environmentId !== ''
        ? defaults.environmentId
        : applyEnvToRequestDefaults.environmentId != null && applyEnvToRequestDefaults.environmentId !== ''
          ? applyEnvToRequestDefaults.environmentId
          : agentSessionState?.environmentId ?? '';
    void refreshProjects();
    setOpen(true);
    setWidgetInlinePanel('apply_env_to_request');
    setApplyEnvToRequestDefaults({
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
      collectionId:
        defaults?.collectionId ??
        collectionsSelection?.collectionId ??
        applyEnvToRequestDefaults.collectionId ??
        '',
      requestId:
        defaults?.requestId ??
        collectionsSelection?.requestId ??
        applyEnvToRequestDefaults.requestId ??
        '',
      environmentId: resolvedEnvironmentId,
      key: defaults?.key ?? applyEnvToRequestDefaults.key ?? APPLY_ENV_ALL_KEYS,
    });
  };

  const openWidgetExecuteRequestForm = (defaults?: {
    projectId?: number | '';
    projectName?: string;
    collectionId?: number | '';
    requestId?: number | '';
    environmentId?: number | '';
  }) => {
    const resolvedProjectId =
      defaults?.projectId != null && defaults.projectId !== ''
        ? defaults.projectId
        : executeRequestDefaults.projectId != null && executeRequestDefaults.projectId !== ''
          ? executeRequestDefaults.projectId
          : agentSessionState?.projectId ?? selectedProject?.id ?? '';
    const resolvedProjectName =
      defaults?.projectName?.trim() ||
      executeRequestDefaults.projectName?.trim() ||
      agentSessionState?.projectName?.trim() ||
      selectedProject?.name ||
      '';
    const resolvedEnvironmentId =
      defaults?.environmentId != null && defaults.environmentId !== ''
        ? defaults.environmentId
        : executeRequestDefaults.environmentId != null && executeRequestDefaults.environmentId !== ''
          ? executeRequestDefaults.environmentId
          : agentSessionState?.environmentId ?? '';
    setOpen(true);
    setWidgetInlinePanel('execute_request');
    setExecuteRequestDefaults({
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
      collectionId: defaults?.collectionId ?? executeRequestDefaults.collectionId ?? '',
      requestId: defaults?.requestId ?? executeRequestDefaults.requestId ?? '',
      environmentId: resolvedEnvironmentId,
    });
  };

  const openWidgetExecuteRequestFormFromCollections = async () => {
    const selection = await resolveAgentCollectionsSelection();
    openWidgetExecuteRequestForm({
      projectId: selection?.projectId ?? '',
      projectName: selection?.projectName,
      collectionId: selection?.collectionId ?? '',
      requestId: selection?.requestId ?? '',
      environmentId: selection?.environmentId ?? '',
    });
  };

  const openWidgetCopyRequestToAllForm = (defaults?: {
    projectId?: number | '';
    projectName?: string;
    collectionId?: number | '';
    requestId?: number | '';
  }) => {
    const resolvedProjectId =
      defaults?.projectId != null && defaults.projectId !== ''
        ? defaults.projectId
        : executeRequestDefaults.projectId != null && executeRequestDefaults.projectId !== ''
          ? executeRequestDefaults.projectId
          : agentSessionState?.projectId ?? selectedProject?.id ?? '';
    const resolvedProjectName =
      defaults?.projectName?.trim() ||
      executeRequestDefaults.projectName?.trim() ||
      agentSessionState?.projectName?.trim() ||
      selectedProject?.name ||
      '';
    setOpen(true);
    setWidgetInlinePanel('copy_request_to_all_collections');
    setExecuteRequestDefaults({
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
      collectionId: defaults?.collectionId ?? executeRequestDefaults.collectionId ?? '',
      requestId: defaults?.requestId ?? executeRequestDefaults.requestId ?? '',
      environmentId: executeRequestDefaults.environmentId ?? '',
    });
  };

  const openWidgetCopyRequestToAllFormFromCollections = async () => {
    const selection = await resolveAgentCollectionsSelection();
    openWidgetCopyRequestToAllForm({
      projectId: selection?.projectId ?? '',
      projectName: selection?.projectName,
      collectionId: selection?.collectionId ?? '',
      requestId: selection?.requestId ?? '',
    });
  };

  const openWidgetProjectPickForm = async (
    action: AgentProjectPickAction,
    defaults?: {
      projectId?: number | '';
      projectName?: string;
      collectionId?: number | typeof AGENT_SCOPE_ALL;
      requestId?: number | typeof AGENT_SCOPE_ALL;
    },
  ) => {
    const collectionsSelection =
      defaults?.collectionId != null || defaults?.requestId != null
        ? readAgentCollectionsSelection()
        : (await resolveAgentCollectionsSelection()) ?? readAgentCollectionsSelection();
    const resolvedProjectId =
      defaults?.projectId != null && defaults.projectId !== ''
        ? defaults.projectId
        : collectionsSelection?.projectId != null
          ? collectionsSelection.projectId
          : projectPickDefaults.projectId != null && projectPickDefaults.projectId !== ''
            ? projectPickDefaults.projectId
            : agentSessionState?.projectId ?? selectedProject?.id ?? '';
    const resolvedProjectName =
      defaults?.projectName?.trim() ||
      collectionsSelection?.projectName?.trim() ||
      projectPickDefaults.projectName?.trim() ||
      agentSessionState?.projectName?.trim() ||
      selectedProject?.name ||
      '';
    const resolvedCollectionId =
      defaults?.collectionId ??
      (collectionsSelection?.collectionId != null
        ? collectionsSelection.collectionId
        : projectPickDefaults.collectionId);
    const resolvedRequestId =
      defaults?.requestId ??
      (collectionsSelection?.requestId != null
        ? collectionsSelection.requestId
        : projectPickDefaults.requestId);
    void refreshProjects();
    setOpen(true);
    setWidgetInlinePanel('project_pick');
    setProjectPickAction(action);
    setProjectPickDefaults({
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
      collectionId: resolvedCollectionId,
      requestId: resolvedRequestId,
    });
  };

  const rememberImportProject = (projectId: number, projectName?: string | null) => {
    setImportSwaggerDefaults((prev) => ({
      ...prev,
      projectId,
      projectName: projectName?.trim() || prev.projectName,
    }));
  };

  const openWidgetImportSwaggerForm = (defaults?: {
    projectId?: number | '';
    projectName?: string;
    swaggerUrl?: string;
  }) => {
    const resolvedProjectId =
      defaults?.projectId != null && defaults.projectId !== ''
        ? defaults.projectId
        : importSwaggerDefaults.projectId != null && importSwaggerDefaults.projectId !== ''
          ? importSwaggerDefaults.projectId
          : agentSessionState?.projectId ?? selectedProject?.id ?? '';
    const resolvedProjectName =
      defaults?.projectName?.trim() ||
      importSwaggerDefaults.projectName?.trim() ||
      agentSessionState?.projectName?.trim() ||
      selectedProject?.name ||
      '';
    void refreshProjects();
    setOpen(true);
    setWidgetInlinePanel('import_swagger');
    setImportSwaggerDefaults({
      swaggerUrl: defaults?.swaggerUrl ?? '',
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
    });
  };

  const openWidgetCreateFunctionForm = (defaults?: { name?: string }) => {
    setOpen(true);
    setWidgetInlinePanel('create_function');
    setCreateFunctionDefaults({ name: defaults?.name ?? '' });
  };

  const openWidgetRunFunctionForm = (defaults?: { name?: string; args?: unknown[] }) => {
    setOpen(true);
    setWidgetInlinePanel('run_function');
    setRunFunctionDefaults({
      name: defaults?.name ?? '',
      args: defaults?.args,
    });
  };

  const appendWidgetAgentUserMessage = (text: string) => {
    setMessages((prev) => {
      const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
      return [...prev, { id: nextId, author: 'user', text }];
    });
  };

  const appendWidgetAgentAssistantReply = async (res: AgentMessageResponse) => {
    const resolvedChatId = res.state?.chatId ?? agentChatIdRef.current ?? agentChatId;
    if (res.state) {
      setAgentSessionState(res.state);
      if (res.state.chatId != null) {
        setAgentChatId(res.state.chatId);
      }
      if (res.state.projectId != null) {
        agentProjectIdRef.current = res.state.projectId;
        rememberImportProject(res.state.projectId, res.state.projectName);
      }
    }
    if (resolvedChatId != null) {
      agentChatIdRef.current = resolvedChatId;
      setAgentChatId(resolvedChatId);
      await loadAgentChatHistory(resolvedChatId, true);
      dispatchAgentContextSync({
        chatId: resolvedChatId,
        projectId: res.state?.projectId ?? null,
        source: 'widget',
      });
    } else {
      const mapped = mapAgentReplyToWidgetMessage(res);
      setMessages((prev) => {
        const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
        return [
          ...prev,
          {
            id: nextId,
            author: 'assistant',
            text: mapped.text,
            agentActions: mapped.agentActions,
            agentFailure: mapped.agentFailure,
            agentSuccess: mapped.agentSuccess,
            hideFeedback: true,
          },
        ];
      });
    }
    if (res.proxyLogs?.length) {
      proxyService.ingestExternalLogs(res.proxyLogs);
    }
  };

  const appendAgentApiReply = (res: AgentMessageResponse) => {
    if (res.state?.chatId != null) {
      setAgentChatId(res.state.chatId);
      setAgentSessionState(res.state);
    }
    if (!res.reply) return;
    const isFailure = Boolean(res.error) || isAgentFailureMessage(res.reply, res.actions);
    setMessages((prev) => {
      const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
      return [
        ...prev,
        {
          id: nextId,
          author: 'assistant',
          text: res.reply!,
          agentActions: res.actions,
          agentFailure: isFailure,
          agentSuccess: !isFailure && isAgentSuccessMessage(res.reply!, res.actions),
          hideFeedback: true,
        },
      ];
    });
    if (res.proxyLogs?.length) {
      proxyService.ingestExternalLogs(res.proxyLogs);
    }
    void applyWidgetIntentResolution(res);
  };

  const applyWidgetIntentResolution = async (res: AgentMessageResponse) => {
    const resolution = res.intentResolution;
    if (!resolution) return;
    if (resolution.needsConfirmation) {
      rememberOfferedAgentIntentId(resolution.intentId);
      return;
    }
    if (resolution.source !== 'fuzzy' && resolution.source !== 'llm' && resolution.source !== 'confirm') {
      return;
    }
    if (!resolution.intentId || resolution.intentId === 'unknown') return;
    rememberOfferedAgentIntentId(resolution.intentId);

    const formVariant = resolution.formVariant;
    if (formVariant === 'create_select') {
      openWidgetCreateSelectForm(
        resolution.mode === 'existing' ? 'existing' : resolution.mode === 'new' ? 'new' : 'new',
      );
      return;
    }
    if (formVariant === 'import_swagger') {
      openWidgetImportSwaggerForm({
        projectId: res.state?.projectId ?? agentSessionState?.projectId ?? '',
        projectName: res.state?.projectName ?? agentSessionState?.projectName ?? '',
      });
      return;
    }
    if (formVariant === 'create_environment') {
      openWidgetCreateEnvironmentForm({
        projectId: res.state?.projectId ?? agentSessionState?.projectId ?? '',
        projectName: res.state?.projectName ?? agentSessionState?.projectName ?? '',
      });
      return;
    }
    if (formVariant === 'env_variable') {
      openWidgetEnvVariableForm({
        projectId: res.state?.projectId ?? agentSessionState?.projectId ?? '',
        environmentId: res.state?.environmentId ?? agentSessionState?.environmentId ?? '',
      });
      return;
    }
    if (formVariant === 'apply_env_to_request' || formVariant === 'apply_env_variables') {
      openWidgetApplyEnvToRequestForm({
        projectId: res.state?.projectId ?? agentSessionState?.projectId ?? '',
        environmentId: res.state?.environmentId ?? agentSessionState?.environmentId ?? '',
      });
      return;
    }
    if (formVariant === 'execute_request') {
      await openWidgetExecuteRequestFormFromCollections();
      return;
    }
    if (formVariant === 'copy_request_to_all_collections') {
      await openWidgetCopyRequestToAllFormFromCollections();
      return;
    }
    if (formVariant === 'create_function') {
      openWidgetCreateFunctionForm();
      return;
    }
    if (formVariant === 'run_function') {
      openWidgetRunFunctionForm();
      return;
    }
    if (resolution.outcome === 'project_pick' && resolution.backendAction) {
      if (isWidgetInlineProjectPickAction(resolution.backendAction)) {
        void openWidgetProjectPickForm(resolution.backendAction as AgentProjectPickAction, {
          projectId: res.state?.projectId ?? agentSessionState?.projectId ?? '',
          projectName: res.state?.projectName ?? agentSessionState?.projectName ?? '',
        });
      }
    }
  };

  const runAgentShortcutInWidget = async (text: string): Promise<boolean> => {
    const collectionsSelection = readAgentCollectionsSelection();
    const shortcut = interpretAgentShortcut(text, {
      hasSelectedRequest: collectionsSelection?.requestId != null,
      hasSelectedProject:
        agentSessionState?.projectId != null || selectedProject?.id != null,
      lastOfferedIntentId: readLastOfferedAgentIntentId(),
    });
    if (!shortcut) return false;

    if (shortcut.kind === 'form' && shortcut.variant === 'create_select') {
      openWidgetCreateSelectForm(shortcut.mode ?? 'new');
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'import_swagger') {
      openWidgetImportSwaggerForm();
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'create_environment') {
      openWidgetCreateEnvironmentForm();
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'env_variable') {
      openWidgetEnvVariableForm();
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'apply_env_to_request') {
      openWidgetApplyEnvToRequestForm();
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'execute_request') {
      void openWidgetExecuteRequestFormFromCollections();
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'copy_request_to_all_collections') {
      void openWidgetCopyRequestToAllFormFromCollections();
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'create_function') {
      openWidgetCreateFunctionForm();
      return true;
    }

    if (shortcut.kind === 'form' && shortcut.variant === 'run_function') {
      openWidgetRunFunctionForm();
      return true;
    }

    if (shortcut.kind === 'project_pick' && WIDGET_INLINE_PROJECT_PICK_ACTIONS.has(shortcut.action)) {
      void openWidgetProjectPickForm(shortcut.action, {
        projectId: agentSessionState?.projectId ?? selectedProject?.id ?? '',
        projectName: agentSessionState?.projectName ?? selectedProject?.name ?? '',
      });
      return true;
    }

    if (shortcut.kind === 'action') {
      // Proxy health must be checked in the browser — the backend cannot see localhost:9000.
      if (shortcut.action === 'diagnose_proxy') {
        setLoading(true);
        try {
          const report = await runProxyDiagnostics();
          setMessages((prev) => {
            const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
            return [
              ...prev,
              {
                id: nextId,
                author: 'assistant',
                text: report.agentReply,
                agentFailure: !report.healthy,
                agentSuccess: report.healthy,
                hideFeedback: true,
              },
            ];
          });
        } catch {
          setMessages((prev) => {
            const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
            return [
              ...prev,
              {
                id: nextId,
                author: 'assistant',
                text: 'Failed to run proxy diagnostics.',
                agentFailure: true,
                hideFeedback: true,
              },
            ];
          });
        } finally {
          setLoading(false);
        }
        return true;
      }
      setLoading(true);
      try {
        const res = await apiService.agentMessage({
          action: shortcut.action,
          chatId: agentChatId ?? undefined,
          payload: shortcut.data,
        });
        if (!res.error) {
          notifyAgentSideEffectsFromAgent(shortcut.action, shortcut.data, res.state);
        }
        appendAgentApiReply(res);
      } catch {
        setMessages((prev) => {
          const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
          return [
            ...prev,
            {
              id: nextId,
              author: 'assistant',
              text: 'Sorry, something went wrong while running that action.',
              agentFailure: true,
              hideFeedback: true,
            },
          ];
        });
      } finally {
        setLoading(false);
      }
      return true;
    }

    navigateToAgentPage(navigationStateFromShortcut(shortcut, agentChatId));
    return true;
  };

  const handleAgentActionClick = async (action: AgentActionButton) => {
    if (loading) return;

    if (action.id === 'run_single_request') {
      const payload = action.payload ?? {};
      const rawProjectId = payload.projectId;
      const projectId =
        typeof rawProjectId === 'number'
          ? rawProjectId
          : typeof rawProjectId === 'string' && rawProjectId !== ''
            ? Number(rawProjectId)
            : '';
      const rawCollectionId = payload.collectionId;
      const collectionId =
        typeof rawCollectionId === 'number'
          ? rawCollectionId
          : typeof rawCollectionId === 'string' && rawCollectionId !== ''
            ? Number(rawCollectionId)
            : '';
      const rawRequestId = payload.requestId;
      const requestId =
        typeof rawRequestId === 'number'
          ? rawRequestId
          : typeof rawRequestId === 'string' && rawRequestId !== ''
            ? Number(rawRequestId)
            : '';
      const rawEnvironmentId = payload.environmentId;
      const environmentId =
        typeof rawEnvironmentId === 'number'
          ? rawEnvironmentId
          : typeof rawEnvironmentId === 'string' && rawEnvironmentId !== ''
            ? Number(rawEnvironmentId)
            : '';
      openWidgetExecuteRequestForm({
        projectId: projectId !== '' && !Number.isNaN(projectId) ? projectId : '',
        projectName: agentSessionState?.projectName ?? selectedProject?.name ?? '',
        collectionId: collectionId !== '' && !Number.isNaN(collectionId) ? collectionId : '',
        requestId: requestId !== '' && !Number.isNaN(requestId) ? requestId : '',
        environmentId: environmentId !== '' && !Number.isNaN(environmentId) ? environmentId : '',
      });
      return;
    }

    if (action.id === 'run_collection') {
      const payload = (action.payload ?? {}) as Record<string, unknown>;
      const parseId = (value: unknown): number | undefined => {
        if (typeof value === 'number' && !Number.isNaN(value) && value > 0) return value;
        if (typeof value === 'string' && value.trim() !== '') {
          const n = Number(value);
          if (!Number.isNaN(n) && n > 0) return n;
        }
        return undefined;
      };
      const projectId =
        parseId(payload.projectId) ??
        (typeof agentSessionState?.projectId === 'number' ? agentSessionState.projectId : undefined) ??
        selectedProject?.id;
      const collectionId = parseId(payload.collectionId);
      const requestId = parseId(payload.requestId);
      const environmentId =
        parseId(payload.environmentId) ??
        (typeof agentSessionState?.environmentId === 'number'
          ? agentSessionState.environmentId
          : undefined);

      if (environmentId != null && environmentId > 0) {
        try {
          sessionStorage.setItem('collectionsAgentEnvironmentId', String(environmentId));
        } catch {
          /* ignore */
        }
      }

      if (projectId != null) {
        const projectRow = projects.find((p) => p.id === projectId);
        if (projectRow) {
          setSelectedProject(projectRow);
        }
      }

      // Always navigate — the previous handler only scrolled (and only on Collections),
      // so Open Collections appeared non-clickable from the floating assistant.
      navigate('/collections', {
        state: {
          fromAiAgent: true,
          projectId: projectId ?? null,
          firstCollectionId: collectionId ?? null,
          firstRequestId: requestId ?? null,
          environmentId: environmentId ?? null,
          chatId: agentChatId ?? null,
        },
      });
      return;
    }

    const pageNav = navigationStateFromAgentAction(action, agentChatId);
    if (pageNav?.agentForm === 'create_select') {
      openWidgetCreateSelectForm();
      return;
    }
    if (pageNav?.agentForm === 'import_swagger') {
      const payload = pageNav.agentFormPayload ?? {};
      const rawProjectId = payload.projectId;
      const projectId =
        typeof rawProjectId === 'number'
          ? rawProjectId
          : typeof rawProjectId === 'string' && rawProjectId !== ''
            ? Number(rawProjectId)
            : '';
      openWidgetImportSwaggerForm({
        projectId: projectId !== '' && !Number.isNaN(projectId) ? projectId : '',
        projectName:
          typeof payload.projectName === 'string'
            ? payload.projectName
            : agentSessionState?.projectName ?? selectedProject?.name ?? '',
        swaggerUrl: typeof payload.swaggerUrl === 'string' ? payload.swaggerUrl : '',
      });
      return;
    }
    if (pageNav?.agentForm === 'create_environment') {
      const payload = pageNav.agentFormPayload ?? {};
      const rawProjectId = payload.projectId;
      const projectId =
        typeof rawProjectId === 'number'
          ? rawProjectId
          : typeof rawProjectId === 'string' && rawProjectId !== ''
            ? Number(rawProjectId)
            : '';
      openWidgetCreateEnvironmentForm({
        projectId: projectId !== '' && !Number.isNaN(projectId) ? projectId : '',
        projectName:
          typeof payload.projectName === 'string'
            ? payload.projectName
            : agentSessionState?.projectName ?? selectedProject?.name ?? '',
      });
      return;
    }
    if (pageNav?.agentForm === 'env_variable') {
      const payload = pageNav.agentFormPayload ?? {};
      const rawProjectId = payload.projectId;
      const projectId =
        typeof rawProjectId === 'number'
          ? rawProjectId
          : typeof rawProjectId === 'string' && rawProjectId !== ''
            ? Number(rawProjectId)
            : '';
      const rawEnvironmentId = payload.environmentId;
      const environmentId =
        typeof rawEnvironmentId === 'number'
          ? rawEnvironmentId
          : typeof rawEnvironmentId === 'string' && rawEnvironmentId !== ''
            ? Number(rawEnvironmentId)
            : '';
      openWidgetEnvVariableForm({
        projectId: projectId !== '' && !Number.isNaN(projectId) ? projectId : '',
        projectName:
          typeof payload.projectName === 'string'
            ? payload.projectName
            : agentSessionState?.projectName ?? selectedProject?.name ?? '',
        environmentId: environmentId !== '' && !Number.isNaN(environmentId) ? environmentId : '',
        key: typeof payload.key === 'string' ? payload.key : undefined,
        value: typeof payload.value === 'string' ? payload.value : undefined,
      });
      return;
    }
    if (pageNav?.agentForm === 'apply_env_variables' || pageNav?.agentForm === 'apply_env_to_request') {
      const payload = pageNav.agentFormPayload ?? {};
      const rawProjectId = payload.projectId;
      const projectId =
        typeof rawProjectId === 'number'
          ? rawProjectId
          : typeof rawProjectId === 'string' && rawProjectId !== ''
            ? Number(rawProjectId)
            : '';
      const rawEnvironmentId = payload.environmentId;
      const environmentId =
        typeof rawEnvironmentId === 'number'
          ? rawEnvironmentId
          : typeof rawEnvironmentId === 'string' && rawEnvironmentId !== ''
            ? Number(rawEnvironmentId)
            : '';
      const rawCollectionId = payload.collectionId;
      const collectionId =
        typeof rawCollectionId === 'number'
          ? rawCollectionId
          : typeof rawCollectionId === 'string' && rawCollectionId !== ''
            ? Number(rawCollectionId)
            : '';
      const rawRequestId = payload.requestId;
      const requestId =
        typeof rawRequestId === 'number'
          ? rawRequestId
          : typeof rawRequestId === 'string' && rawRequestId !== ''
            ? Number(rawRequestId)
            : '';
      openWidgetApplyEnvToRequestForm({
        projectId: projectId !== '' && !Number.isNaN(projectId) ? projectId : '',
        projectName:
          typeof payload.projectName === 'string'
            ? payload.projectName
            : agentSessionState?.projectName ?? selectedProject?.name ?? '',
        collectionId: collectionId !== '' && !Number.isNaN(collectionId) ? collectionId : '',
        requestId: requestId !== '' && !Number.isNaN(requestId) ? requestId : '',
        environmentId: environmentId !== '' && !Number.isNaN(environmentId) ? environmentId : '',
        key: typeof payload.key === 'string' ? payload.key : undefined,
      });
      return;
    }
    if (pageNav?.agentForm === 'copy_request_to_all_collections') {
      const payload = pageNav.agentFormPayload ?? {};
      const rawProjectId = payload.projectId;
      const projectId =
        typeof rawProjectId === 'number'
          ? rawProjectId
          : typeof rawProjectId === 'string' && rawProjectId !== ''
            ? Number(rawProjectId)
            : '';
      const rawCollectionId = payload.collectionId;
      const collectionId =
        typeof rawCollectionId === 'number'
          ? rawCollectionId
          : typeof rawCollectionId === 'string' && rawCollectionId !== ''
            ? Number(rawCollectionId)
            : '';
      const rawRequestId = payload.requestId;
      const requestId =
        typeof rawRequestId === 'number'
          ? rawRequestId
          : typeof rawRequestId === 'string' && rawRequestId !== ''
            ? Number(rawRequestId)
            : '';
      openWidgetCopyRequestToAllForm({
        projectId: projectId !== '' && !Number.isNaN(projectId) ? projectId : '',
        projectName:
          typeof payload.projectName === 'string'
            ? payload.projectName
            : agentSessionState?.projectName ?? selectedProject?.name ?? '',
        collectionId: collectionId !== '' && !Number.isNaN(collectionId) ? collectionId : '',
        requestId: requestId !== '' && !Number.isNaN(requestId) ? requestId : '',
      });
      return;
    }
    if (pageNav?.agentForm === 'execute_request') {
      const payload = pageNav.agentFormPayload ?? {};
      const rawProjectId = payload.projectId;
      const projectId =
        typeof rawProjectId === 'number'
          ? rawProjectId
          : typeof rawProjectId === 'string' && rawProjectId !== ''
            ? Number(rawProjectId)
            : '';
      const rawEnvironmentId = payload.environmentId;
      const environmentId =
        typeof rawEnvironmentId === 'number'
          ? rawEnvironmentId
          : typeof rawEnvironmentId === 'string' && rawEnvironmentId !== ''
            ? Number(rawEnvironmentId)
            : '';
      const rawCollectionId = payload.collectionId;
      const collectionId =
        typeof rawCollectionId === 'number'
          ? rawCollectionId
          : typeof rawCollectionId === 'string' && rawCollectionId !== ''
            ? Number(rawCollectionId)
            : '';
      const rawRequestId = payload.requestId;
      const requestId =
        typeof rawRequestId === 'number'
          ? rawRequestId
          : typeof rawRequestId === 'string' && rawRequestId !== ''
            ? Number(rawRequestId)
            : '';
      openWidgetExecuteRequestForm({
        projectId: projectId !== '' && !Number.isNaN(projectId) ? projectId : '',
        projectName:
          typeof payload.projectName === 'string'
            ? payload.projectName
            : agentSessionState?.projectName ?? selectedProject?.name ?? '',
        collectionId: collectionId !== '' && !Number.isNaN(collectionId) ? collectionId : '',
        requestId: requestId !== '' && !Number.isNaN(requestId) ? requestId : '',
        environmentId: environmentId !== '' && !Number.isNaN(environmentId) ? environmentId : '',
      });
      return;
    }
    if (pageNav?.agentForm === 'create_function') {
      openWidgetCreateFunctionForm({
        name: typeof pageNav.agentFormPayload?.name === 'string' ? pageNav.agentFormPayload.name : '',
      });
      return;
    }
    if (pageNav?.agentForm === 'run_function') {
      const payload = pageNav.agentFormPayload ?? {};
      openWidgetRunFunctionForm({
        name: typeof payload.name === 'string' ? payload.name : '',
        args: Array.isArray(payload.args) ? payload.args : undefined,
      });
      return;
    }
    if (pageNav) {
      navigateToAgentPage(pageNav);
      return;
    }

    if (isWidgetInlineProjectPickAction(action.id)) {
      void openWidgetProjectPickForm(action.id, {
        projectId:
          typeof action.payload?.projectId === 'number'
            ? action.payload.projectId
            : agentSessionState?.projectId ?? selectedProject?.id ?? '',
        projectName:
          typeof action.payload?.projectName === 'string'
            ? action.payload.projectName
            : agentSessionState?.projectName ?? selectedProject?.name ?? '',
      });
      return;
    }

    setLoading(true);
    try {
      const res = await apiService.agentMessage({
        action: action.id,
        chatId: agentChatId ?? undefined,
        payload: action.payload,
      });
      if (!res.error) {
        notifyAgentSideEffectsFromAgent(action.id, action.payload, res.state);
      }
      appendAgentApiReply(res);
    } catch {
      setMessages((prev) => {
        const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
        return [
          ...prev,
          {
            id: nextId,
            author: 'assistant',
            text: 'Sorry, something went wrong while running that action.',
            agentFailure: true,
            hideFeedback: true,
          },
        ];
      });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (text: string, options?: { displayText?: string }) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const nextId = messages.length ? messages[messages.length - 1].id + 1 : 1;
    const userMessage: ChatMessage = {
      id: nextId,
      author: 'user',
      text: options?.displayText ?? trimmed,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const localInfoAnswer = getAgentInfoHelpAnswer(trimmed);
    if (localInfoAnswer) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextId + 1,
          author: 'assistant',
          text: localInfoAnswer,
        },
      ]);
      setHelpFollowUps([]);
      setLoading(false);
      return;
    }

    try {
      if (agentMode) {
        const handled = await runAgentShortcutInWidget(trimmed);
        if (handled) return;

        const res: AgentMessageResponse = await apiService.agentMessage({
          message: trimmed,
          chatId: agentChatId ?? undefined,
          payload: withAgentIntentContextPayload(undefined, {
            sessionProjectId: agentSessionState?.projectId ?? selectedProject?.id ?? null,
          }),
        });
        // Shared reply path also applies Phase 1/2 intentResolution (forms / project-pick).
        appendAgentApiReply(res);
        setHelpFollowUps([]);
      } else {
        const response = await apiService.askHelpChat(trimmed, {
          moduleId: helpContext.moduleId,
          subModuleId: helpContext.subModuleId,
          sessionId: helpSessionId,
        });
        let safeAnswer = (response?.answer || '').trim()
          || 'I could not generate a help response. Please try rephrasing your question.';
        const topicFallback = getAgentInfoHelpAnswer(trimmed);
        if (topicFallback && isGenericHelpResponse(safeAnswer, trimmed)) {
          safeAnswer = topicFallback;
        }
        const assistantMessage: ChatMessage = {
          id: nextId + 1,
          author: 'assistant',
          text: safeAnswer,
          intentMode: response?.intentMode,
          confidence: response?.confidence,
          lowConfidence: response?.lowConfidence,
          citations: Array.isArray(response?.citations)
            ? response.citations
              .filter((c) => !!c && typeof c.title === 'string' && typeof c.helpPath === 'string')
              .slice(0, 4)
            : [],
          actions: Array.isArray(response?.actions)
            ? response.actions
              .filter((a) => !!a && typeof a.label === 'string' && typeof a.targetPath === 'string')
              .slice(0, 3)
            : [],
        };
        setHelpFollowUps(
          Array.isArray(response?.followUps)
            ? response.followUps.filter((q) => typeof q === 'string' && q.trim().length > 0).slice(0, 6)
            : [],
        );
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (err) {
      if (!agentMode) {
        setHelpFollowUps([]);
      }
      const assistantMessage: ChatMessage = {
        id: nextId + 1,
        author: 'assistant',
        text: agentMode
          ? 'Sorry, something went wrong while talking to the AI Agent.'
          : 'Sorry, something went wrong while looking up the documentation. You can still open the full Help page from the sidebar.',
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } finally {
      setLoading(false);
      if (agentMode) {
        clearAgentQuickAction();
      }
    }
  };

  const handleSend = async () => {
    await sendMessage(input);
  };

  const runAgentQuickAction = async () => {
    if (!agentQuickAction || loading) return;
    const actionId = agentQuickAction;
    // Dismiss any previously opened (but not submitted) form before starting another action.
    closeWidgetInlinePanel();
    if (actionId === 'create_select') {
      openWidgetCreateSelectForm();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'import_swagger') {
      openWidgetImportSwaggerForm();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'create_environment') {
      openWidgetCreateEnvironmentForm();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'env_variable') {
      openWidgetEnvVariableForm();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'apply_env_variables' || actionId === 'apply_env_to_request') {
      void openWidgetApplyEnvToRequestForm();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'create_function') {
      openWidgetCreateFunctionForm();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'run_function') {
      openWidgetRunFunctionForm();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'suggest_assertions') {
      void openWidgetProjectPickForm('suggest_datasets');
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'default_headers') {
      void openWidgetProjectPickForm('apply_default_headers');
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'bearer') {
      void openWidgetProjectPickForm('apply_bearer');
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'apply_ordering') {
      void openWidgetProjectPickForm('apply_ordering');
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'execute_request') {
      await openWidgetExecuteRequestFormFromCollections();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'copy_request_to_all_collections') {
      await openWidgetCopyRequestToAllFormFromCollections();
      clearAgentQuickAction();
      return;
    }
    if (actionId === 'diagnose_proxy') {
      const label = 'Diagnose proxy connection';
      setMessages((prev) => {
        const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
        return [...prev, { id: nextId, author: 'user', text: label }];
      });
      setLoading(true);
      try {
        const report = await runProxyDiagnostics();
        setMessages((prev) => {
          const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
          return [
            ...prev,
            {
              id: nextId,
              author: 'assistant',
              text: report.agentReply,
              agentFailure: !report.healthy,
              agentSuccess: report.healthy,
              hideFeedback: true,
            },
          ];
        });
      } catch {
        setMessages((prev) => {
          const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
          return [
            ...prev,
            {
              id: nextId,
              author: 'assistant',
              text: 'Failed to run proxy diagnostics.',
              agentFailure: true,
              hideFeedback: true,
            },
          ];
        });
      } finally {
        setLoading(false);
        clearAgentQuickAction();
      }
      return;
    }
    const option = agentQuickActions.find((o) => o.id === actionId);
    if (!option) return;
    await sendMessage(option.prompt, { displayText: option.label });
  };

  const handleSuggestionClick = async (question: string) => {
    await sendMessage(question);
  };

  const submitFeedback = async (messageId: number, helpful: boolean, reasonTag?: string) => {
    try {
      await apiService.submitHelpChatFeedback({
        sessionId: helpSessionId,
        messageId,
        helpful,
        reasonTag,
        moduleId: helpContext.moduleId,
        intentMode: messages.find((m) => m.id === messageId)?.intentMode,
      });
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, feedbackSubmitted: true } : m)));
    } catch {
      // keep silent in chat; feedback is best-effort.
    }
  };

  useEffect(() => {
    return () => {
      if (enableOlderLoadTimerRef.current != null) {
        window.clearTimeout(enableOlderLoadTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (skipScrollToBottomRef.current) {
      skipScrollToBottomRef.current = false;
      return;
    }
    if (agentMode) {
      if (loadingAgentHistory) return;
      scheduleAgentScrollToBottom();
      scheduleEnableOlderAgentMessageLoad();
      return;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, messages, loading, agentMode, loadingAgentHistory]);

  // Auto-open AI assistant when arriving from AI Agent "Guide run flow"
  useEffect(() => {
    const state: any = (location as any).state;
    if (state && state.fromAiAgent && !open) {
      setOpen(true);
    }
  }, [location, open]);

  // When coming from AI Agent, switch to agent mode and load the selected project's chat
  useEffect(() => {
    const state: any = (location as any).state;
    if (!state || !state.fromAiAgent || initializedFromAgent) return;

    (async () => {
      try {
        setHelpFollowUps([]);
        setAgentMode(true);
        await loadAgentModeContext();
      } catch (err) {
        console.error('Failed to preload Help assistant with AI Agent history', err);
      } finally {
        setInitializedFromAgent(true);
      }
    })();
  }, [location, initializedFromAgent, loadAgentModeContext]);

  const visibleSuggestions = useMemo(
    () => (helpFollowUps.length > 0 && !agentMode ? helpFollowUps : suggestionExamples),
    [helpFollowUps, suggestionExamples, agentMode],
  );

  const agentQuickActions = useMemo(
    () => buildAgentWidgetQuickActions(pageKey),
    [pageKey],
  );

  const buttonStyle = useMemo(
    () => ({
      right: FAB_OFFSET,
      bottom: FAB_OFFSET,
    }),
    [],
  );

  const panelStyle = useMemo(
    () => ({
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: '100%',
      height: '100%',
    }),
    [],
  );

  return (
    <Box
      sx={{
        // Docked beside the page when open so the main column shrinks instead of being covered.
        flex: open ? '0 0 auto' : '0 0 0',
        width: open ? ASSISTANT_STRIP_WIDTH_PX : 0,
        maxWidth: open ? ASSISTANT_STRIP_MAX_VW : 0,
        height: open ? '100%' : 0,
        minWidth: 0,
        minHeight: 0,
        alignSelf: 'stretch',
        overflow: open ? 'hidden' : 'visible',
        position: 'relative',
        pointerEvents: open ? 'auto' : 'none',
        zIndex: (theme) => theme.zIndex.drawer + 2,
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {!open && (
        <Box
          sx={{
            position: 'fixed',
            ...buttonStyle,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <Tooltip title="Open AI assistant">
            <IconButton
              color="primary"
              size="large"
              onClick={toggleOpen}
              aria-label="Open AI assistant"
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }}
            >
              <SmartToyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {open && (
        <Box
          sx={{
            position: 'absolute',
            ...panelStyle,
            boxSizing: 'border-box',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: 'background.paper',
          }}
        >
          <Card
            elevation={0}
            sx={{
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              border: 'none',
              borderLeft: '1px solid rgba(15, 23, 42, 0.12)',
              boxShadow: 'none',
              backgroundColor: '#f7f6f1',
              backgroundImage:
                'radial-gradient(circle at 16px 16px, rgba(99, 102, 241, 0.08) 1.2px, transparent 1.2px)',
              backgroundSize: '18px 18px',
              overflow: 'hidden',
            }}
          >
            <CardHeader
              sx={{
                flexShrink: 0,
                alignItems: 'flex-start',
                '& .MuiCardHeader-content': { minWidth: 0 },
                '& .MuiCardHeader-action': { flexShrink: 0, ml: 1, alignSelf: 'flex-start' },
              }}
              title={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {agentMode ? 'AI Agent Assistant' : 'Help assistant'}
                </Typography>
              }
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {agentMode && (
                    <Tooltip title="Open full AI Agent page">
                      <IconButton
                        onClick={() => navigate('/ai-agent', { state: { chatId: agentChatId } })}
                        size="small"
                        aria-label="Open AI Agent page"
                      >
                        <SmartToyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton onClick={toggleOpen} size="small" aria-label="Close help assistant">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            />
            <Box sx={{ px: 1.5, pb: 1, flexShrink: 0, display: 'flex', gap: 1, alignItems: 'stretch' }}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={agentMode ? 'agent' : 'help'}
                onChange={(_, value) => {
                  if (value === 'help') void switchAssistantMode(false);
                  if (value === 'agent') void switchAssistantMode(true);
                }}
                aria-label="Assistant mode"
                sx={{ flex: 1 }}
              >
                <ToggleButton value="help" aria-label="Help assistant">
                  <HelpOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Help
                </ToggleButton>
                <ToggleButton value="agent" aria-label="AI Agent assistant">
                  <SmartToyIcon fontSize="small" sx={{ mr: 0.5 }} />
                  AI Agent
                </ToggleButton>
              </ToggleButtonGroup>
              {agentMode && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleNewAgentChat()}
                  disabled={loading}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0, px: 1.25 }}
                >
                  New Chat
                </Button>
              )}
            </Box>
            <Divider />
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.5,
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                ref={messagesScrollRef}
                onScroll={agentMode ? handleAgentMessagesScroll : undefined}
                sx={{
                  flex: '1 1 0%',
                  // Keep a little chat history visible when a tall action form is open.
                  minHeight: widgetInlinePanel ? 48 : 0,
                  overflowY: 'auto',
                  mb: 0.5,
                  pr: 0.5,
                }}
              >
                {agentMode && hasMoreOlderAgentMessages && (
                  <Box sx={{ textAlign: 'center', py: 0.5, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {loadingOlderAgentMessages ? 'Loading older messages…' : 'Scroll up for older messages'}
                    </Typography>
                  </Box>
                )}
                {messages.length === 0 && !loading && !loadingAgentHistory && (
                  <Box
                    sx={{
                      mb: 1,
                      px: 1,
                      py: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.82)',
                      border: '1px solid rgba(15,23,42,0.08)',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        mx: 'auto',
                        mb: 0.75,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                        color: '#fff',
                      }}
                    >
                      <SmartToyIcon fontSize="small" />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', lineHeight: 1.35 }}>
                      {agentMode ? 'Good to see you' : 'Need quick help?'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.35 }}>
                      {agentMode
                        ? 'Use quick actions below or ask a question.'
                        : 'Answers based on in-app documentation.'}
                    </Typography>
                  </Box>
                )}
                {messages.map((msg) => {
                  const agentFailure = msg.author === 'assistant' && msg.agentFailure === true;
                  const agentSuccess = msg.author === 'assistant' && !agentFailure && msg.agentSuccess === true;
                  const visibleAgentActions = agentMode
                    ? filterAgentActionsForWidget(msg.agentActions)
                    : msg.agentActions;
                  const useStructuredAgentResult =
                    agentMode &&
                    msg.author === 'assistant' &&
                    parseAgentExecutionResult(msg.text) != null;
                  return (
                  <Box
                    key={msg.serverMessageId ?? `local-${msg.id}`}
                    sx={{
                      mb: 1,
                      display: 'flex',
                      justifyContent: msg.author === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '85%',
                        px: 1.6,
                        py: msg.author === 'assistant' ? 1.1 : 0.75,
                        borderRadius: 2.5,
                        bgcolor:
                          msg.author === 'user'
                            ? '#4f46e5'
                            : agentFailure
                              ? 'error.50'
                              : agentSuccess
                                ? 'success.50'
                                : 'rgba(255,255,255,0.92)',
                        color:
                          msg.author === 'user'
                            ? 'primary.contrastText'
                            : agentFailure
                              ? 'error.main'
                              : agentSuccess
                                ? 'success.main'
                                : 'text.primary',
                        whiteSpace: msg.author === 'assistant' ? 'normal' : 'pre-line',
                        lineHeight: 1.5,
                        boxShadow: msg.author === 'assistant' ? '0 6px 16px rgba(15, 23, 42, 0.10)' : 'none',
                        border: msg.author === 'assistant'
                          ? agentFailure || agentSuccess
                            ? '1px solid'
                            : '1px solid rgba(15,23,42,0.08)'
                          : 'none',
                        borderColor: agentFailure
                          ? 'error.light'
                          : agentSuccess
                            ? 'success.light'
                            : 'rgba(15,23,42,0.08)',
                      }}
                    >
                      {msg.author === 'assistant' ? (
                        <>
                          {useStructuredAgentResult ? (
                            <AgentExecutionResultMessage
                              text={msg.text}
                              failure={agentFailure}
                              success={agentSuccess}
                            />
                          ) : (
                            <MarkdownMessage
                              text={msg.text}
                              tone={agentFailure ? 'error' : agentSuccess ? 'success' : 'default'}
                            />
                          )}
                          {(msg.intentMode || typeof msg.confidence === 'number') && (
                            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {msg.intentMode && <Chip size="small" label={msg.intentMode.toLowerCase()} />}
                              {typeof msg.confidence === 'number' && (
                                <Chip
                                  size="small"
                                  color={msg.lowConfidence ? 'warning' : 'default'}
                                  label={`confidence ${(msg.confidence * 100).toFixed(0)}%`}
                                />
                              )}
                            </Box>
                          )}
                          {!!msg.citations?.length && (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary">
                                Sources
                              </Typography>
                              <List dense sx={{ mt: 0.25, pt: 0 }}>
                                {msg.citations.map((c) => (
                                  <ListItemButton
                                    key={`${msg.id}-${c.helpPath}-${c.title}`}
                                    sx={{ py: 0.1, px: 0.5, borderRadius: 1 }}
                                    onClick={() => navigate(c.helpPath)}
                                  >
                                    <ListItemText
                                      primary={c.title}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItemButton>
                                ))}
                              </List>
                            </Box>
                          )}
                          {!!msg.actions?.length && (
                            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {msg.actions.map((a) => (
                                <Button
                                  key={`${msg.id}-${a.id}`}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => navigate(a.targetPath)}
                                >
                                  {a.label}
                                </Button>
                              ))}
                            </Box>
                          )}
                          {!!visibleAgentActions?.length && (
                            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {visibleAgentActions.map((a) => {
                                const confirmChip = isIntentConfirmAction(a);
                                const chipColor = agentFailure
                                  ? 'error'
                                  : agentSuccess
                                    ? 'success'
                                    : 'primary';
                                return (
                                <Button
                                  key={`${msg.id}-${a.id}-${a.label}`}
                                  size="small"
                                  variant={confirmChip ? 'contained' : 'outlined'}
                                  color={chipColor}
                                  onClick={() => handleAgentActionClick(a)}
                                  disabled={loading}
                                  sx={{
                                    whiteSpace: 'normal',
                                    textAlign: 'center',
                                    lineHeight: 1.25,
                                    py: 0.5,
                                    maxWidth: '100%',
                                    ...(confirmChip ? { fontWeight: 600 } : null),
                                    ...(agentSuccess && !confirmChip
                                      ? {
                                          borderColor: 'success.light',
                                          color: 'success.main',
                                          '&:hover': { bgcolor: 'success.50' },
                                        }
                                      : null),
                                    ...(agentFailure && !confirmChip
                                      ? {
                                          borderColor: 'error.light',
                                          color: 'error.main',
                                          '&:hover': { bgcolor: 'error.50' },
                                        }
                                      : null),
                                  }}
                                >
                                  {formatAgentActionLabel(a.id, a.label)}
                                </Button>
                                );
                              })}
                            </Box>
                          )}
                          {!msg.hideFeedback && !msg.feedbackSubmitted && (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary">
                                Was this helpful?
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  icon={<ThumbUpAltOutlinedIcon />}
                                  label="Yes"
                                  onClick={() => submitFeedback(msg.id, true)}
                                />
                                <Chip
                                  size="small"
                                  icon={<ThumbDownAltOutlinedIcon />}
                                  label="No"
                                  onClick={() => submitFeedback(msg.id, false)}
                                />
                                <Chip size="small" label="Wrong module" onClick={() => submitFeedback(msg.id, false, 'wrong_module')} />
                                <Chip size="small" label="Too long" onClick={() => submitFeedback(msg.id, false, 'too_long')} />
                                <Chip size="small" label="Not actionable" onClick={() => submitFeedback(msg.id, false, 'not_actionable')} />
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (
                        msg.text
                      )}
                    </Box>
                  </Box>
                  );
                })}
                {(loading || loadingAgentHistory) && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Suggested questions / quick actions — pinned below scroll area */}
              <Box sx={{ mb: 0.5, flexShrink: 0 }}>
                {!!visibleSuggestions.length && !agentMode && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.35, fontWeight: 500 }}>
                      {helpFollowUps.length > 0 ? 'Suggested follow-ups' : 'Suggestions'}
                    </Typography>
                    <List dense sx={{ mt: 0.5 }}>
                      {visibleSuggestions.map((question) => (
                        <ListItemButton
                          key={question}
                          onClick={() => handleSuggestionClick(question)}
                          sx={{
                            py: 0.55,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.9)',
                            border: '1px solid rgba(15,23,42,0.08)',
                            mb: 0.5,
                          }}
                        >
                          <ListItemText
                            primary={question}
                            primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontSize: '0.85rem', lineHeight: 1.35, fontWeight: 500, whiteSpace: 'normal', wordBreak: 'break-word' },
                          }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </>
                )}
                {agentMode && (
                  <Box sx={{ mt: 0.25 }}>
                    {widgetInlinePanel && (
                      <Box
                        sx={{
                          // Cap height so Choose-an-action + Ask box stay on screen.
                          maxHeight: 'min(26vh, 220px)',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          pr: 0.5,
                          mb: 0.75,
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                    {widgetInlinePanel === 'create_select' && (
                      <AgentWidgetCreateSelectProjectForm
                        key={`create-select-${widgetCreateSelectMode}`}
                        chatId={agentChatId}
                        initialSelectionMode={widgetCreateSelectMode}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onProjectResolved={(project) => rememberImportProject(project.id, project.name)}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'import_swagger' && (
                      <AgentWidgetImportSwaggerForm
                        key={`import-swagger-${importSwaggerDefaults.projectId ?? 'none'}-${importSwaggerDefaults.projectName ?? ''}`}
                        chatId={agentChatId}
                        defaultProjectId={importSwaggerDefaults.projectId ?? ''}
                        defaultProjectName={importSwaggerDefaults.projectName ?? ''}
                        defaultSwaggerUrl={importSwaggerDefaults.swaggerUrl ?? ''}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'create_environment' && (
                      <AgentWidgetCreateEnvironmentForm
                        key={`create-environment-${createEnvironmentDefaults.projectId ?? 'none'}`}
                        chatId={agentChatId}
                        defaultProjectId={createEnvironmentDefaults.projectId ?? ''}
                        defaultProjectName={createEnvironmentDefaults.projectName ?? ''}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'env_variable' && (
                      <AgentWidgetEnvVariableForm
                        key={`env-variable-${envVariableDefaults.projectId ?? 'none'}-${envVariableDefaults.environmentId ?? 'none'}`}
                        chatId={agentChatId}
                        defaultProjectId={envVariableDefaults.projectId ?? ''}
                        defaultProjectName={envVariableDefaults.projectName ?? ''}
                        defaultEnvironmentId={envVariableDefaults.environmentId ?? ''}
                        defaultKey={envVariableDefaults.key ?? ''}
                        defaultValue={envVariableDefaults.value ?? ''}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'apply_env_to_request' && (
                      <AgentWidgetApplyEnvToRequestForm
                        key={`apply-env-req-${applyEnvToRequestDefaults.projectId ?? 'none'}-${applyEnvToRequestDefaults.requestId ?? 'none'}`}
                        chatId={agentChatId}
                        defaultProjectId={applyEnvToRequestDefaults.projectId ?? ''}
                        defaultProjectName={applyEnvToRequestDefaults.projectName ?? ''}
                        defaultCollectionId={applyEnvToRequestDefaults.collectionId ?? ''}
                        defaultRequestId={applyEnvToRequestDefaults.requestId ?? ''}
                        defaultEnvironmentId={applyEnvToRequestDefaults.environmentId ?? ''}
                        defaultKey={applyEnvToRequestDefaults.key ?? APPLY_ENV_ALL_KEYS}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'execute_request' && (
                      <AgentWidgetExecuteRequestForm
                        key={`execute-request-${executeRequestDefaults.projectId ?? 'none'}-${executeRequestDefaults.requestId ?? 'none'}`}
                        chatId={agentChatId}
                        defaultProjectId={executeRequestDefaults.projectId ?? ''}
                        defaultProjectName={executeRequestDefaults.projectName ?? ''}
                        defaultCollectionId={executeRequestDefaults.collectionId ?? ''}
                        defaultRequestId={executeRequestDefaults.requestId ?? ''}
                        defaultEnvironmentId={executeRequestDefaults.environmentId ?? ''}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'copy_request_to_all_collections' && (
                      <AgentWidgetCopyRequestToAllCollectionsForm
                        key={`copy-request-all-${executeRequestDefaults.projectId ?? 'none'}-${executeRequestDefaults.requestId ?? 'none'}`}
                        chatId={agentChatId}
                        defaultProjectId={executeRequestDefaults.projectId ?? ''}
                        defaultProjectName={executeRequestDefaults.projectName ?? ''}
                        defaultCollectionId={executeRequestDefaults.collectionId ?? ''}
                        defaultRequestId={executeRequestDefaults.requestId ?? ''}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'project_pick' && projectPickAction && (
                      <AgentWidgetProjectPickForm
                        key={`project-pick-${projectPickAction}-${projectPickDefaults.projectId ?? 'none'}-${projectPickDefaults.collectionId ?? 'all'}-${projectPickDefaults.requestId ?? 'all'}`}
                        action={projectPickAction}
                        chatId={agentChatId}
                        defaultProjectId={projectPickDefaults.projectId ?? ''}
                        defaultProjectName={projectPickDefaults.projectName ?? ''}
                        defaultCollectionId={projectPickDefaults.collectionId}
                        defaultRequestId={projectPickDefaults.requestId}
                        onClose={() => {
                          setWidgetInlinePanel(null);
                          setProjectPickAction(null);
                        }}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'create_function' && (
                      <AgentWidgetCreateFunctionForm
                        key={`create-function-${createFunctionDefaults.name ?? 'new'}`}
                        chatId={agentChatId}
                        defaultName={createFunctionDefaults.name ?? ''}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                    {widgetInlinePanel === 'run_function' && (
                      <AgentWidgetRunFunctionForm
                        key={`run-function-${runFunctionDefaults.name ?? 'any'}`}
                        chatId={agentChatId}
                        defaultName={runFunctionDefaults.name ?? ''}
                        defaultArgs={runFunctionDefaults.args}
                        onClose={() => setWidgetInlinePanel(null)}
                        onUserMessage={appendWidgetAgentUserMessage}
                        onAgentReply={appendWidgetAgentAssistantReply}
                        onChatIdChange={(id) => {
                          agentChatIdRef.current = id;
                          setAgentChatId(id);
                        }}
                        onLoadingChange={setLoading}
                      />
                    )}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <AgentInlineSelect
                          id="widget-agent-action-select"
                          label="Choose an action"
                          fullWidth
                          value={agentQuickAction}
                          disabled={loading}
                          maxListHeight={360}
                          options={agentQuickActions.map((option) => ({
                            value: option.id,
                            label: option.label,
                          }))}
                          onChange={(raw) => {
                            setAgentQuickAction(raw);
                            closeWidgetInlinePanel();
                          }}
                          sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                        />
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={runAgentQuickAction}
                        disabled={loading || !agentQuickAction}
                        sx={{
                          flexShrink: 0,
                          minWidth: 72,
                          alignSelf: 'stretch',
                          textTransform: 'none',
                          bgcolor: 'rgba(255,255,255,0.9)',
                        }}
                      >
                        Go
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 0.5,
                  p: 0.5,
                  flex: '0 0 auto',
                  flexShrink: 0,
                  borderRadius: 999,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(15,23,42,0.1)',
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder={agentMode ? 'Ask the AI Agent or use quick actions below…' : 'Ask about this page...'}
                  type="text"
                  name="help-chat-question"
                  autoComplete="off"
                  inputProps={{ autoComplete: 'off' }}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!input.trim() || loading) return;
                      void handleSend();
                    }
                  }}
                  disabled={loading}
                  sx={{
                    ...environmentPlaceholderSx,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      bgcolor: 'transparent',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.8125rem',
                    },
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={() => void handleSend()}
                  disabled={!input.trim() || loading}
                  sx={{ bgcolor: 'rgba(79,70,229,0.12)' }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>

            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default HelpChatWidget;

